import classNames from "classnames"
import { useMemo, useRef, useState, useEffect, forwardRef, useCallback, ReactNode } from "react"
import { useLocation } from "react-router"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectIsAccountPopoutOpen, selectIsWorkspaceSwitcherOpen } from "./selectors"
import {
    closeAccountPopout,
    closeWorkspaceSwitcher,
    openAccountPopout,
    openWorkspaceSwitcher,
} from "./slice"
import { AddOns, Licenses, zIndex } from "@nova/constants"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import {
    selectActiveWorkspaceId,
    selectAllWorkspaces,
    setActiveFlyoutMenuId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectUserFullName } from "features/entity-caching/members/UserManagementSlice"
import { selectUserAddOns, selectUserEmail, selectUserLicense } from "user/profile/selectors"
import { NovaRouteTree, useRouter } from "pages"
import { GetScopedTo } from "@nova/react-router"
import { LicenseBadge } from "@nova/ds-component-license-badge"
import { AddOnIcon } from "plugins/AddonIcon"
import { ButtonTertiary, IconButton } from "@nova/component-btn"
import { ScrollableArea } from "@nova/component-scrollable-area"
import packageJson from "../../../package.json"
import { download } from "tools/browser/download"
import { summarize } from "@nova/use-debug"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { showGlobalModal } from "runlevel/actions"
import { logout } from "user/authentication/thunks"
import { OrganizationBasic } from "@nova/schemas"
import { ExpandableContent } from "@nova/component-expandable-content"
import { InputPill } from "@nova/ds-component-input"
import { NotificationsDrawer } from "panels/notifications/NotificationsDrawer"
import { useFeatureFlag, featureFlagTogglesFlag } from "features/feature-flags/useFeatureFlag"
import { useObserveElementRect } from "@nova/toolkit-pure-hooks"

type NavTarget = "home" | "overview" | "explore" | "fleet" | "settings" | "notifications"
type NavItem = HTMLElement | undefined
export function GlobalNavSidebar() {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const [activeTarget, setActiveTarget] = useState<NavTarget>()
    const previousTarget = useRef<NavTarget>(undefined)
    const [canTransitionActiveIndicator, setCanTransitionActiveIndicator] = useState(true)
    useEffect(() => {
        const previousNavTarget = previousTarget.current
        let newTarget: NavTarget = "home"
        const pathname = location.pathname
        if (pathname.includes("overview")) {
            newTarget = "overview"
        }
        if (pathname.includes("explore")) {
            newTarget = "explore"
        }
        if (pathname.includes("fleet")) {
            newTarget = "fleet"
        }
        if (pathname.includes("settings")) {
            newTarget = "settings"
        }
        if (newTarget !== previousNavTarget) {
            setCanTransitionActiveIndicator(
                (newTarget === "settings" && previousNavTarget === "settings") ||
                    (newTarget !== "settings" && previousNavTarget !== "settings")
            )
            previousTarget.current = activeTarget
        }
        setActiveTarget(newTarget)
    }, [activeTarget, location.pathname])
    const itemRefs = useRef<Record<NavTarget, NavItem>>({
        home: undefined,
        overview: undefined,
        explore: undefined,
        fleet: undefined,
        settings: undefined,
        notifications: undefined,
    })
    const accountPopoutOpen = useAppSelector(selectIsAccountPopoutOpen)
    const { ref: containerRef, ...containerRect } = useObserveElementRect()
    const itemRect = activeTarget && itemRefs.current?.[activeTarget]?.getBoundingClientRect()
    const activePosition = itemRect && {
        top: itemRect.top - containerRect.top,
        left: itemRect.left - containerRect.left,
    }
    const featureFlagTogglesEnabled = useFeatureFlag(featureFlagTogglesFlag)
    return (
        <div
            ref={containerRef}
            className="h-full flex flex-col gap-5 pt-2 pb-[28px] justify-center items-center isolate transition-all px-4 relative"
        >
            <div className="h-[75px] flex justify-center items-center border-b border-b-gray-700">
                <div
                    className="cursor-pointer group"
                    onClick={() => dispatch(openWorkspaceSwitcher())}
                >
                    <Icons.LargeIcon
                        Icon={Icons.NovaLogo}
                        className="stroke-transparent group-hover:stroke-white-400 group-hover:drop-shadow-[0_2px_12px_rgba(241,255,102,0.5)] transition-all"
                    />
                </div>
            </div>
            <WorkspaceSwitcherWrapper />
            <GlobalNavSidebarItem
                ref={(element) => {
                    itemRefs.current.home = element || undefined
                }}
                to={{ path: "/" }}
                Icon={Icons.Home}
                isActive={activeTarget === "home"}
            />
            <GlobalNavSidebarItem
                ref={(element) => {
                    itemRefs.current.overview = element || undefined
                }}
                to={{ path: "/overview" }}
                Icon={Icons.CaptivePortal}
                isActive={activeTarget === "overview"}
            />
            <GlobalNavSidebarItem
                ref={(element) => {
                    itemRefs.current.explore = element || undefined
                }}
                to={{ path: "/explore/ws" }}
                Icon={Icons.Folder}
                isActive={activeTarget === "explore"}
            />
            <GlobalNavSidebarItem
                ref={(element) => {
                    itemRefs.current.fleet = element || undefined
                }}
                to={{ path: "/fleet" }}
                Icon={Icons.Graph7}
                isActive={activeTarget === "fleet"}
            />
            <div className="w-full grow border-b border-b-gray-700" />

            <NotificationsDrawer
                direction="left"
                renderTrigger={(hasUnreadNotifications) => (
                    <GlobalNavSidebarItem
                        ref={(element) => {
                            itemRefs.current.notifications = element || undefined
                        }}
                        to={null}
                        Icon={
                            hasUnreadNotifications ? Icons.NotificationStatus : Icons.Notification
                        }
                        isActive={false}
                    />
                )}
            />
            <GlobalNavSidebarItem
                ref={(element) => {
                    itemRefs.current.settings = element || undefined
                }}
                to={{ path: "/settings" }}
                Icon={Icons.Gear}
                isActive={activeTarget === "settings"}
            />
            {featureFlagTogglesEnabled && (
                <GlobalNavSidebarItem
                    to={null}
                    Icon={Icons.Tune}
                    isActive={false}
                    onClick={() => {
                        dispatch(
                            initializeCenterModalForComponentAction(
                                ModalNames.FeatureFlagToggleModal
                            )
                        )
                        dispatch(showGlobalModal())
                    }}
                />
            )}
            <div
                className="cursor-pointer w-6 aspect-square rounded overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() =>
                    dispatch(accountPopoutOpen ? closeAccountPopout() : openAccountPopout())
                }
            >
                <ProfilePhoto self />
            </div>
            <AccountPopoutWrapper />
            <div
                className={classNames("w-8 h-8 rounded-lg absolute z-0", {
                    "transition ease-in-out": activePosition && canTransitionActiveIndicator,
                })}
                style={{
                    top: 0,
                    left: 0,
                    transform: `translate(${activePosition?.left}px, ${activePosition?.top}px)`,
                    opacity: activePosition ? 1 : 0,
                }}
            >
                <Icons.LargeIcon Icon={Icons.ActiveGlobalNavBorder} />
            </div>
        </div>
    )
}

const WorkspaceSwitcherWrapper = () => {
    const dispatch = useAppDispatch()
    const workspaceSwitcherOpen = useAppSelector(selectIsWorkspaceSwitcherOpen)
    useEffect(() => {
        if (typeof window === "undefined" || !workspaceSwitcherOpen) return
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                dispatch(closeWorkspaceSwitcher())
            }
        }
        window.addEventListener("keydown", handleKeyDown)
        return () => {
            window.removeEventListener("keydown", handleKeyDown)
        }
    }, [dispatch, workspaceSwitcherOpen])
    return (
        <div
            className={classNames(
                "fixed bg-black-800 inset-0 transition-opacity duration-[400ms]",
                {
                    "opacity-0 pointer-events-none": !workspaceSwitcherOpen,
                }
            )}
            style={{ backdropFilter: "blur(10px)", zIndex: zIndex.HighPriorityModal }}
            onClick={() => dispatch(closeWorkspaceSwitcher())}
        >
            <div
                className={classNames("transition-transform duration-[400ms] h-full", {
                    "-translate-x-full": !workspaceSwitcherOpen,
                })}
            >
                <WorkspaceSwitcher />
            </div>
        </div>
    )
}

const WorkspaceSwitcher = () => {
    const dispatch = useAppDispatch()
    const workspaceSwitcherOpen = useAppSelector(selectIsWorkspaceSwitcherOpen)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const [isSearchOpen, setIsSearchOpen] = useState(false)
    const [search, setSearch] = useState("")
    useEffect(() => {
        if (!isSearchOpen) setSearch("")
        if (isSearchOpen && searchInputRef.current) {
            searchInputRef.current.focus({ preventScroll: true })
        }
    }, [isSearchOpen])
    useEffect(() => {
        if (!workspaceSwitcherOpen) {
            setIsSearchOpen(false)
        }
    }, [workspaceSwitcherOpen])
    const workspaces = useAppSelector(selectAllWorkspaces())
    const sortedAndFilteredWorkspaces = useMemo(() => {
        return workspaces
            .filter(
                (workspace) =>
                    !search || workspace.name.toLowerCase().includes(search.toLowerCase())
            )
            .toSorted((a, b) => a.name.localeCompare(b.name))
    }, [search, workspaces])
    const [major, minor, patch] = packageJson.version.split(".")
    const formatVersion = () => {
        let version = `${major}.${minor}`
        if (patch && patch !== "0") {
            version += `.${patch}`
        }
        return version
    }
    return (
        <div
            className="h-full max-h-full p-12 bg-black-700 w-0 min-w-[550px] md:w-1/2 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex gap-2 items-center shrink-0">
                <div
                    className="flex items-center gap-2 group cursor-pointer"
                    onClick={() => dispatch(closeWorkspaceSwitcher())}
                >
                    <div className="w-9 aspect-square rounded-full bg-white-100 group-hover:bg-black-900 group-hover:shadow-[0_0_12px_0_rgba(255,255,255,0.25)] transition-[background,box-shadow] flex items-center justify-center">
                        <Icons.LargeIcon
                            Icon={Icons.Close}
                            iconClassName="text-gray-400 group-hover:text-white transition-colors m-0"
                        />
                    </div>
                    <small className="xs text-gray-400 group-hover:text-gray-200 transition-colors">
                        CANCEL
                    </small>
                </div>
                <small className="xs text-gray-500">/ CHANGE WORKSPACE</small>
                <div className="grow" />
                <ButtonTertiary
                    LeftIcon={isSearchOpen ? Icons.XClose : Icons.MagnifyingGlass}
                    onClick={() => setIsSearchOpen((p) => !p)}
                    className={classNames("!px-2 !min-w-0", {
                        "!border-gray-300": isSearchOpen,
                    })}
                />
                <ButtonTertiary
                    title="Workspace"
                    LeftIcon={Icons.Plus}
                    onClick={() => {
                        dispatch(
                            initializeCenterModalForComponentAction(
                                ModalNames.OrganizationEditorModal
                            )
                        )
                        dispatch(showGlobalModal())
                        dispatch(setActiveFlyoutMenuId(undefined))
                    }}
                />
            </div>
            <div className="shrink-0">
                <ExpandableContent isOpen={isSearchOpen}>
                    <div className="p-px pb-6">
                        <InputPill
                            ref={searchInputRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="text-white !rounded-full telegraf"
                            placeholder="Search workspaces"
                            size="lg"
                            placeholderColor="dark"
                            leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} />}
                            rightItem={
                                !!search ? (
                                    <div className="h-full flex items-center text-white-300">
                                        <IconButton onClick={() => setSearch("")}>
                                            <Icons.Icon Icon={Icons.Cancel} />
                                        </IconButton>
                                    </div>
                                ) : undefined
                            }
                        />
                    </div>
                </ExpandableContent>
            </div>
            <ScrollableArea className="h-full relative">
                <div
                    className="sticky w-full h-5 top-0 z-20"
                    style={{
                        background:
                            "linear-gradient(0deg, rgba(12, 12, 15, 0.00) 0%, #0F0F11 100%)",
                    }}
                />
                <div className="flex flex-col gap-px">
                    {sortedAndFilteredWorkspaces.map((workspace) => (
                        <WorkspaceSwitcherOption key={workspace.id} workspace={workspace} />
                    ))}
                    {!sortedAndFilteredWorkspaces.length && (
                        <div className="flex flex-col gap-2 py-2 items-center">
                            <div className="text-3xl telegraf text-white">No workspaces found</div>
                            <div className="text-sm telegraf text-gray-300">
                                Try adjusting your keywords.
                            </div>
                        </div>
                    )}
                </div>
                <div
                    className="sticky w-full h-5 bottom-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(12, 12, 15, 0.00) 0%, #0F0F11 100%)",
                    }}
                />
            </ScrollableArea>
            <div className="flex items-end gap-4 shrink-0">
                <div className="flex flex-col">
                    <small className="text-gray-300 py-1.5">Help & Resources</small>
                    <div
                        className="flex gap-3 items-center group cursor-pointer py-1.5"
                        onClick={() => window.open("https://learn.mapnova.com", "_blank")}
                    >
                        <Icons.SmallIcon
                            Icon={Icons.Lightbulb}
                            iconClassName="text-gray-300 group-hover:text-blue-500 transition-colors"
                        />
                        <small className="text-white-600 group-hover:text-white transition-colors">
                            Learn Center
                        </small>
                    </div>
                    <div
                        className="flex gap-3 items-center group cursor-pointer py-1.5"
                        onClick={() => window.open("mailto:help@mapnova.com", "_blank")}
                    >
                        <Icons.SmallIcon
                            Icon={Icons.Mail}
                            iconClassName="text-gray-300 group-hover:text-blue-500 transition-colors"
                        />
                        <small className="text-white-600 group-hover:text-white transition-colors">
                            Contact Us
                        </small>
                    </div>
                    <div
                        className="flex gap-3 items-center group cursor-pointer py-1.5"
                        onClick={() => window.open("mailto:info@mapnova.com", "_blank")}
                    >
                        <Icons.SmallIcon
                            Icon={Icons.MessageBubbleQuick}
                            iconClassName="text-gray-300 group-hover:text-blue-500 transition-colors"
                        />
                        <small className="text-white-600 group-hover:text-white transition-colors">
                            Give Feedback
                        </small>
                    </div>
                    <div
                        className="flex gap-3 items-center group cursor-pointer py-1.5"
                        onClick={() =>
                            download("debug.txt", "text/plain;charset=utf-8", summarize())
                        }
                    >
                        <Icons.SmallIcon
                            Icon={Icons.Bug}
                            iconClassName="text-gray-300 group-hover:text-blue-500 transition-colors"
                        />
                        <small className="text-white-600 group-hover:text-white transition-colors">
                            Download Debug
                        </small>
                    </div>
                </div>
                <div className="grow" />
                <small className="xs text-gray-500">Version {formatVersion()}</small>
                <div className="w-[94px] h-[22px] flex justify-center items-center">
                    <Icons.ExtraSmallIcon Icon={Icons.NovaLogoTitle} />
                </div>
            </div>
        </div>
    )
}

const WorkspaceSwitcherOption = ({ workspace }: { workspace: OrganizationBasic }) => {
    const dispatch = useAppDispatch()
    const router = useRouter("/")
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId())
    const isActive = activeWorkspaceId === workspace.id
    const itemRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (isActive && itemRef.current) {
            itemRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
        }
    }, [isActive])
    return (
        <div
            ref={itemRef}
            className={classNames(
                "py-1 flex items-center gap-4 group rounded-2xl transition-colors relative",
                {
                    "cursor-pointer hover:bg-white-100": !isActive,
                }
            )}
            onClick={() => {
                router.navigate({
                    path: "/workspace/:workspaceId",
                    params: { workspaceId: workspace.id },
                })
                dispatch(closeWorkspaceSwitcher())
            }}
        >
            <div className="flex items-center">
                <div
                    className={classNames(
                        "px-2 w-0 flex justify-center items-center opacity-0 transition-[width,opacity]",
                        {
                            "group-hover:w-12 group-hover:opacity-100": !isActive,
                        }
                    )}
                >
                    <Icons.LargeIcon Icon={Icons.WorkspaceArrow} iconClassName="text-[#F1FF66]" />
                </div>
                <div
                    className={classNames("text-4xl", {
                        "text-white-400 group-hover:text-white transition-colors": !isActive,
                        "text-white": isActive,
                    })}
                >
                    {workspace.name}
                </div>
            </div>
            {isActive && (
                <div
                    className="p-px rounded-full"
                    style={{ background: "linear-gradient(0deg, #5DF2AF 0%, #EAF67C 100%)" }}
                >
                    <div className="px-3 py-0.5 bg-gray-900 rounded-full">
                        <small className="xs text-white">CURRENT</small>
                    </div>
                </div>
            )}
        </div>
    )
}

const GlobalNavSidebarItem = forwardRef<
    HTMLDivElement,
    {
        to: GetScopedTo<NovaRouteTree, "/workspace/:workspaceId"> | null
        isActive: boolean
        Icon: Icons.IconComponent
        onClick?: () => void
    }
>(function Item({ to, isActive, Icon, onClick }, ref) {
    const router = useRouter("/workspace/:workspaceId")
    const classes = classNames("transition-colors z-10", {
        "text-white": isActive,
        "text-white-400 hover:text-white": !isActive,
    })

    const content = (
        <div ref={ref} className="w-8 h-8 flex justify-center items-center">
            <Icons.Icon Icon={Icon} />
        </div>
    )

    return to ? (
        <router.Link className={classes} to={to}>
            {content}
        </router.Link>
    ) : (
        <button className={classes} onClick={onClick}>
            {content}
        </button>
    )
})

const AccountPopoutWrapper = () => {
    const accountPopoutOpen = useAppSelector(selectIsAccountPopoutOpen)
    return (
        <div
            className={classNames("fixed bottom-7 left-12", {
                "pointer-events-none opacity-0": !accountPopoutOpen,
            })}
            style={{ zIndex: zIndex.HighPriorityModal }}
        >
            <GlobalNavSidebarAccountPopout />
        </div>
    )
}

const GlobalNavSidebarAccountPopout = () => {
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const location = useLocation()
    const isOnProfileSettingsPage = location.pathname === router.preview({ path: "/settings" })
    const ViewAccountWrapper = useCallback(
        ({ children }: { children: ReactNode }) => {
            if (isOnProfileSettingsPage) {
                return (
                    <div className="cursor-pointer" onClick={() => dispatch(closeAccountPopout())}>
                        {children}
                    </div>
                )
            }
            return <router.Link to={{ path: "/settings" }}>{children}</router.Link>
        },
        [dispatch, isOnProfileSettingsPage, router]
    )
    const userFullName = useAppSelector(selectUserFullName()) || "User"
    const userEmail = useAppSelector(selectUserEmail)
    const license = useAppSelector(selectUserLicense)
    const licenseType = useMemo(() => {
        if (!license) return Licenses.Lite
        switch (license.subscriptionType) {
            case Licenses.Pro:
            case Licenses.Essentials:
            case Licenses.Lite:
                return license.subscriptionType
            default:
                return Licenses.Lite
        }
    }, [license])
    const addOns = useAppSelector(selectUserAddOns)
    const addOnsArray = useMemo(() => {
        return Object.keys(addOns)
            .filter((addOn) => !!addOns[addOn as keyof typeof addOns])
            .map((addOn) => addOn as AddOns)
    }, [addOns])
    useEffect(() => {
        dispatch(closeAccountPopout())
    }, [location.pathname, dispatch])
    return (
        <ClickAwayListenerV2 onClickAway={() => dispatch(closeAccountPopout())}>
            <div className="px-2 pt-2 rounded-[20px] bg-gray-700 border border-white-100 flex flex-col min-w-[240px] shadow-lg">
                <div
                    className="w-full p-px rounded-xl"
                    style={{
                        background: "linear-gradient(0deg, #5DF2AF 0%, #EAF67C 100%)",
                    }}
                >
                    <div className="w-full p-4 bg-gray-900 flex flex-col gap-4 rounded-xl">
                        <div className="w-8 aspect-square rounded-lg overflow-hidden">
                            <ProfilePhoto self />
                        </div>
                        <div className="flex flex-col">
                            <div className="text-lg text-white">{userFullName}</div>
                            <div className="text-xs text-white-600">{userEmail}</div>
                        </div>
                        <div className="flex gap-3">
                            <LicenseBadge license={licenseType} />
                            <div className="flex gap-2">
                                {addOnsArray.map((addOn) => (
                                    <AddOnIcon key={addOn} addOn={addOn} />
                                ))}
                            </div>
                        </div>
                        <ViewAccountWrapper>
                            <div className="flex gap-1 items-center group">
                                <div className="text-white-400 group-hover:text-white transition-colors">
                                    View Account
                                </div>
                                <Icons.SmallIcon
                                    Icon={Icons.ArrowRight}
                                    iconClassName="text-white-400 group-hover:text-blue-500 transition-colors"
                                />
                            </div>
                        </ViewAccountWrapper>
                    </div>
                </div>
                <div className="py-3 flex justify-center items-center">
                    <div
                        className="flex gap-2.5 group cursor-pointer justify-center items-center"
                        onClick={() => dispatch(logout())}
                    >
                        <div className="text-gray-50 group-hover:text-white transition-colors">
                            Logout
                        </div>
                        <Icons.SmallIcon
                            Icon={Icons.Logout}
                            iconClassName="text-white-300 group-hover:text-white-600 transition-colors"
                        />
                    </div>
                </div>
            </div>
        </ClickAwayListenerV2>
    )
}
